import {
  SubmitButton,
  TextField,
  Typography,
  FormErrorMessage,
} from "@avenue-8/sidekick-ui";
import styled from "@emotion/styled";
import { Grid, IconButton, Checkbox, FormControlLabel } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { NumericFormat } from "react-number-format";
import { useMemo, useCallback, useEffect } from "react";
import {
  Controller,
  FieldError,
  useForm,
  useWatch,
  FormProvider,
} from "react-hook-form";
import {
  BEDS_BATHS_MAX_VALUE,
  FindListingsFiltersFormSchema,
  findListingsFiltersFormSchema,
  FindListingsSchema,
  GARAGE_SPACES_MAX_VALUE,
  isNumberAllowed,
  LIVING_SPACE_MAX_VALUE,
  PRICE_MAX_VALUE,
} from "@cma/chat-widgets/find-listings-search/helpers/property-search-filters-form-helpers";
import { yupResolver } from "@hookform/resolvers/yup";
import { DynamicPropertyTypesField } from "./components/DynamicPropertyTypesField";
import { DealTypeField } from "./components/DealTypeField";
import { DateRangePicker } from "./components/DateRangePicker";
import { PropertyStatusField } from "./components/PropertyStatusField";
import { LotSizeField } from "./components/LotSizeField";
import { PropertyTypeDto } from "@shared/apis/property-search/generated";
import { useChatWidgetStore } from "@cma/store/chat-widget.store";
import { PropertyStatusItemDto } from "@shared/apis/sidekick/generated";

const Container = styled.div`
  width: 100%;
  padding-bottom: 2rem;

  ${(p) => p.theme.media.laptopDown} {
    padding-bottom: 5rem;
  }
`;

const FiltersHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0;
`;

const BottomFixedActions = styled.div<{ $drawerState?: string }>`
  margin-top: 1rem;
  position: sticky;
  bottom: 1rem;
  left: 100%;
  display: inline-block;
  background: ${(p) => p.theme.colors.background};

  ${(p) => p.theme.media.laptopDown} {
    position: fixed;
    right: 1rem;
    left: auto;
    bottom: ${(p) => {
      switch (p.$drawerState) {
        case "collapsed":
          return "6.75rem";
        case "half-expanded":
          return "calc(45dvh + 1rem)";
        case "expanded":
          return "none";
      }
    }};
  }
`;

const CloseButton = styled(IconButton)`
  border-radius: 0.5rem;
`;

interface Props {
  defaultValues: FindListingsSchema;
  propertyTypes: PropertyTypeDto[];
  propertyStatuses: PropertyStatusItemDto[];
  onApplyFilters: (data: FindListingsSchema) => void;
  onClose?: () => void;
}

const getMinMaxErrors = (
  minError: FieldError | undefined,
  maxError: FieldError | undefined,
) => {
  if (minError && maxError) {
    return `${minError.message} ${maxError.message}`;
  }
  return minError?.message ?? maxError?.message ?? " ";
};

export const FindListingsFilters = (props: Props) => {
  const {
    defaultValues,
    propertyTypes,
    propertyStatuses,
    onApplyFilters,
    onClose,
  } = props;
  const formMethods = useForm<FindListingsFiltersFormSchema>({
    defaultValues,
    resolver: yupResolver(findListingsFiltersFormSchema),
    reValidateMode: "onChange",
  });
  const {
    formState: { isDirty, errors },
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
  } = formMethods;
  const chatWidgetDrawerState = useChatWidgetStore(
    (store) => store.chatWidgetDrawerState,
  );

  const selectedDealType = useWatch({ control, name: "dealType" });

  const listDateStart = watch("listDateStart");
  const listDateEnd = watch("listDateEnd");
  const soldDateStart = watch("soldDateStart");
  const soldDateEnd = watch("soldDateEnd");

  const mlsPropertyTypes = useMemo(
    () => propertyTypes.filter((pt) => pt.dealType === selectedDealType),
    [propertyTypes, selectedDealType],
  );

  useEffect(() => {
    reset({ ...defaultValues });
  }, [reset, defaultValues]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      // TODO: Improve this confirm modal to use a global one with our design
      const confirmClose = window.confirm(
        "You have unsaved changes. Are you sure you want to close?",
      );
      if (!confirmClose) return;
    }
    onClose?.();
  }, [isDirty, onClose]);

  const handleApplyFilters = useCallback(
    (data: FindListingsFiltersFormSchema) => {
      onApplyFilters(data);
    },
    [onApplyFilters],
  );

  const clearListDates = useCallback(() => {
    setValue("listDateStart", null);
    setValue("listDateEnd", null);
  }, [setValue]);

  const clearSoldDates = useCallback(() => {
    setValue("soldDateStart", null);
    setValue("soldDateEnd", null);
  }, [setValue]);

  return (
    <Container>
      <FiltersHeader>
        <Typography variant="h6">Filters</Typography>
        {onClose && (
          <CloseButton onClick={handleClose} aria-label="close">
            <CloseIcon />
          </CloseButton>
        )}
      </FiltersHeader>

      <FormProvider {...formMethods}>
        <Grid
          container
          columnSpacing={2}
          component="form"
          onSubmit={handleSubmit(handleApplyFilters)}
          id="find-listings-form"
        >
          <Grid item xs={12} sm={12}>
            <PropertyStatusField propertyStatuses={propertyStatuses} />
          </Grid>

          <Grid item xs={12} sm={12}>
            <DateRangePicker
              // label="Listed Date Range"
              startValue={listDateStart}
              endValue={listDateEnd}
              onStartChange={(date) => {
                setValue("listDateStart", date);
                if (!date) return;
                clearSoldDates();
              }}
              onEndChange={(date) => {
                setValue("listDateEnd", date);
                if (!date) return;
                clearSoldDates();
              }}
              startLabel="List Start Date"
              endLabel="List End Date"
              startError={errors.listDateStart?.message}
              endError={errors.listDateEnd?.message}
            />
          </Grid>

          <Grid item xs={12} sm={12}>
            <DateRangePicker
              // label="Sold Date Range"
              startValue={soldDateStart}
              endValue={soldDateEnd}
              onStartChange={(date) => {
                setValue("soldDateStart", date);
                if (!date) return;
                clearListDates();
              }}
              onEndChange={(date) => {
                setValue("soldDateEnd", date);
                if (!date) return;
                clearListDates();
              }}
              startLabel="Sold Start Date"
              endLabel="Sold End Date"
              startError={errors.soldDateStart?.message}
              endError={errors.soldDateEnd?.message}
            />
          </Grid>

          <Grid item xs={4} sm={4}>
            <DealTypeField propertyTypes={propertyTypes} />
          </Grid>

          <Grid item xs={8} sm={8}>
            <DynamicPropertyTypesField
              selectedDealType={selectedDealType}
              propertyTypes={mlsPropertyTypes}
            />
          </Grid>

          <Grid container item columnSpacing={2}>
            <Grid item xs={12} mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                Price
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="priceMin"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    fieldError={errors.priceMin}
                    label="Minimum"
                    placeholder="No Min"
                    fullWidth
                    thousandSeparator=","
                    prefix="$"
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.priceMin}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, PRICE_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="priceMax"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    fieldError={errors.priceMax}
                    label="Maximum"
                    placeholder="No Max"
                    fullWidth
                    thousandSeparator=","
                    prefix="$"
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.priceMax}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, PRICE_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>
          </Grid>

          <Grid container item md={6} xs={12} columnSpacing={2}>
            <Grid item xs={12} mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                Bedrooms
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="bedsMin"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Minimum"
                    placeholder="No Min"
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.bedsMin}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, BEDS_BATHS_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="bedsMax"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Maximum"
                    placeholder="No Max"
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.bedsMax}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, BEDS_BATHS_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormErrorMessage>
                {getMinMaxErrors(errors.bedsMin, errors.bedsMax)}
              </FormErrorMessage>
            </Grid>
          </Grid>

          <Grid container item md={6} xs={12} columnSpacing={2}>
            <Grid item xs={12} mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                Bathrooms
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="bathsMin"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Minimum"
                    placeholder="No Min"
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.bathsMin}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, BEDS_BATHS_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="bathsMax"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Maximum"
                    placeholder="No Max"
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.bathsMax}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, BEDS_BATHS_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormErrorMessage>
                {getMinMaxErrors(errors.bathsMin, errors.bathsMax)}
              </FormErrorMessage>
            </Grid>
          </Grid>

          <Grid container item columnSpacing={2}>
            <Grid item xs={12} mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                Living Space (sq ft)
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="livingSpaceMin"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Minimum"
                    placeholder="No Min"
                    fullWidth
                    thousandSeparator=","
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.livingSpaceMin}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, LIVING_SPACE_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="livingSpaceMax"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Maximum"
                    placeholder="No Max"
                    fullWidth
                    thousandSeparator=","
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.livingSpaceMax}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, LIVING_SPACE_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormErrorMessage>
                {getMinMaxErrors(errors.livingSpaceMin, errors.livingSpaceMax)}
              </FormErrorMessage>
            </Grid>
          </Grid>

          <LotSizeField />

          <Grid container item xs={12} md={6} columnSpacing={2}>
            <Grid item xs={12} mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                Garage Spaces
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="garageSpacesMin"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Minimum"
                    placeholder="No Min"
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.garageSpacesMin}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, GARAGE_SPACES_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="garageSpacesMax"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Maximum"
                    placeholder="No Max"
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.garageSpacesMax}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, GARAGE_SPACES_MAX_VALUE)
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormErrorMessage>
                {getMinMaxErrors(
                  errors.garageSpacesMin,
                  errors.garageSpacesMax,
                )}
              </FormErrorMessage>
            </Grid>
          </Grid>

          <Grid container item xs={12} md={6} columnSpacing={2}>
            <Grid item xs={12} mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                Year Built
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="yearBuiltMin"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Minimum"
                    placeholder="No Min"
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.yearBuiltMin}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, new Date().getFullYear())
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={6}>
              <Controller
                name="yearBuiltMax"
                control={control}
                render={({ field: { ref, ...rest } }) => (
                  <NumericFormat
                    customInput={TextField}
                    hideHelperTextSpace
                    label="Maximum"
                    placeholder="No Max"
                    fullWidth
                    allowNegative={false}
                    decimalScale={0}
                    error={!!errors.yearBuiltMax}
                    onValueChange={(v) => rest.onChange(v.value)}
                    {...rest}
                    getInputRef={ref}
                    isAllowed={({ floatValue }) =>
                      isNumberAllowed(floatValue, new Date().getFullYear())
                    }
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormErrorMessage>
                {getMinMaxErrors(errors.yearBuiltMin, errors.yearBuiltMax)}
              </FormErrorMessage>
            </Grid>
          </Grid>

          <Grid container item xs={12} columnSpacing={2}>
            <Grid item xs={12} mb={1}>
              <Typography variant="subtitle1" fontWeight={700}>
                Features
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="waterfrontClassification"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value === "waterfront"}
                        onChange={(e) =>
                          field.onChange(e.target.checked ? "waterfront" : null)
                        }
                      />
                    }
                    label="Waterfront"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
      </FormProvider>

      <BottomFixedActions $drawerState={chatWidgetDrawerState}>
        <Grid container item justifyContent="flex-end">
          <SubmitButton form="find-listings-form">Apply Filters</SubmitButton>
        </Grid>
      </BottomFixedActions>
    </Container>
  );
};
